import {TableCell} from '@tiptap/extension-table-cell';
import {Injector} from "@angular/core";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Command, KeyboardShortcutCommand, Node} from "@tiptap/core";
import {Plugin, PluginKey} from '@tiptap/pm/state'
import {CellSelection} from 'prosemirror-tables';
import {RiktekstRedigeringTabellCelleKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/komponenter/tabellcelle/tabellcelle";
import {AngularNodeViewRenderer} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/ngx-tiptap/src/lib/NodeViewRenderer";
import {Node as ProseMirrorNode, TableMap} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/prosemirror-eksporter";
import {første, siste} from "felles-ng2/støttefunksjoner/lister";
import {NodeType} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/TipTapEditor";
import {erIkkeTomStreng, trimTilTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {styleAttributterForMarg} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleMarger";
import {styleAttributterForBakgrunn} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleBakgrunn";
import {styleAttributterForRamme} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/tiptap/utvidelser/TabellcelleRamme";

/**
 * Delegerer uttegning av td-noder til {@link RiktekstRedigeringTabellCelleKomponent} som beriker td med menyer for redigering av tabell.
 * Merk at prosemirror ikke støtter at en node kan inneholde BÅDE block og inline barn.
 * TableCell er definert med "content: 'block+'", tabellceller vil derfor alltid innehold
 * en <p> eller annet blokkelement. I ckeditor kunne tabellceller inneholde også kun ren tekst,
 * dette får vi ikke uten å opprette en ny TableCell variant som brukes for celler med inline-innhold.
 * Se https://discuss.prosemirror.net/t/how-to-make-a-block-node-contain-arbitrary-inline-children-or-block-children-not-mixing-block-and-inline-node/2036/15
 */
export const TabellCelle = (injector: Injector): Node => {
    return TableCell.extend({

        priority: 200, //standard er 100 - nødvendig med høyere prioritering for at Tab-tastaturkommando registrert her skal vinne over Indentering.ts

        addNodeView: function () {
            return AngularNodeViewRenderer(RiktekstRedigeringTabellCelleKomponent,
              {
                  injector,

                  /** Sørger her for at html-attributt-representasjonen av alle node-attributter blir lagt til td-elementet.
                   * Se {@link }AngularNodeView#updateElementAttributes} og @tiptap/core/src/helpers/getRenderedAttributes.ts */
                  attrs: ({HTMLAttributes}) => HTMLAttributes,

                  /** Kalles av rammeverket for å avgjøre om hendelse skal håndteres av tiptap eller ei.
                   * Vi overlater ansvaret for å stoppe propagering av eventer som oppstår i guien til RiktekstRedigeringTabellCelleKomponent
                   * til RiktekstRedigeringTabellCelleKomponent, og sier her at alle eventer som ikke har blitt stoppet i RiktekstRedigeringTabellCelleKomponent
                   * skal boble opp til tiptap.
                   * Stopper ikke alle eventer her da tableColumnResizing pluginet som aktiveres via https://www.tiptap.dev/api/nodes/table
                   * ellers ikke vil fungere. */
                  stopEvent: (event) => false // ikke stopp eventer...
              });
        },

        //se 'interface TabellcelleKommandoer' lenger ned for dokumentasjon
        addCommands(): TabellcelleKommandoer<Command> {
            return {
                ...this.parent?.(),

                velgCelle: (cellePosisjon: number) => ({dispatch, tr}) => {
                    if (erNil(dispatch)) return true; //kommandoen kalles i dry-run-modus

                    tr.setSelection(CellSelection.create(tr.doc, cellePosisjon));
                    return true;
                },

                velgRad: (cellePosisjon: number) => ({dispatch, tr}) => {
                    if (erNil(dispatch)) return true; //kommandoen kalles i dry-run-modus

                    tr.setSelection(CellSelection.rowSelection(tr.doc.resolve(cellePosisjon)));
                    return true;
                },

                velgKolonne: (cellePosisjon: number) => ({dispatch, tr}) => {
                    if (erNil(dispatch)) return true; //kommandoen kalles i dry-run-modus

                    tr.setSelection(CellSelection.colSelection(tr.doc.resolve(cellePosisjon)));
                    return true;
                },

                velgAlleCeller: (cellePosisjon: number) => (props) => {
                    const tabellNode = props.editor.$pos(cellePosisjon).closest(NodeType.Table)
                    if (erNil(tabellNode)) return false;
                    if (erNil(props.dispatch)) return true; //kommandoen kalles i dry-run-modus

                    let tabellInfo = TableMap.get(tabellNode.node);
                    //tabellInfo.map inneholder offsettet i forhold til tabellNode absolutt posisjon,
                    //legger derfor til tabellNode.pos for å få den absolutte posisjonen til første og siste celle i tabellen
                    props.tr.setSelection(CellSelection.create(props.tr.doc, tabellNode.pos + første(tabellInfo.map)!, tabellNode.pos + siste(tabellInfo.map)!));
                    return true;
                }
            }
        },

        /** Naviger mellom celler vhja tab-tasten.
         * Legger til ny rad dersom man trykker tab når man er i siste celle
         * se https://www.toptal.com/developers/keycode for navn på taster */
        addKeyboardShortcuts(): Record<string, KeyboardShortcutCommand> {
            return {
                Tab: ({editor}) => {
                    if (editor.can().goToNextCell()) return editor.commands.goToNextCell();
                    return editor.chain().addRowAfter().goToNextCell().run();
                },

                /** Sletter tabellraden dersom denne ikke inneholder noen tekst */
                Backspace: ({editor}) => {
                    if (!editor.can().deleteRow()) return false;
                    const tabellcelle = editor.$pos(editor.state.selection.from).closest(NodeType.TableCell)

                    //ikke slett dersom cellen ikke er tom
                    if (erNil(tabellcelle) || erIkkeTomStreng(tabellcelle.textContent)) return false;

                    //finn kolonneindeksen
                    const tabellNode = editor.$pos(editor.state.selection.from).closest(NodeType.Table)!
                    const tableMap = TableMap.get(tabellNode.node);
                    const kolonneindeks = tableMap.colCount(tabellcelle.pos - tabellNode.pos - 1)

                    //dersom vi er i første celle i raden (altså første kolonne) sletter vi raden
                    if (kolonneindeks == 0)
                        return editor.commands.deleteRow();
                    else
                        return editor.commands.goToPreviousCell();
                }
            };
        },

        addAttributes() {
            return {
                ...this.parent?.(),

                /** Td kan ha style attributt */
                style: {
                    default: null,
                    parseHTML: element =>
                      trimTilTomStreng(element.getAttribute('style')).split(';')
                        .map(stil => stil.trim())
                        .map(stil => stil.split(':'))
                        //utelat style attributter som håndteres av andre av tabellcelle-utvidelser
                        .filter(([attributt, verdi]) => ![...styleAttributterForBakgrunn, ...styleAttributterForMarg, ...styleAttributterForRamme].includes(attributt))
                        //tillater et minimum sett av style-attributter, merk at vi ikke har impl gui for å redigere disse..
                        //redigering av width er støttet via tableColumnResizing som benyttes av Tabell, men denne setter bredde via colgroup-htmlnode-innslag
                        //ved innliming fra word vil width være satt på td
                        .filter(([attributt, verdi]) => ['width', 'min-width', 'height', 'min-height', 'vertical-align'].includes(attributt))
                        .map(([attributt, verdi]) => `${attributt}: ${verdi}`)
                        .join(';'),
                    renderHTML: attributes => erIkkeNil(attributes.style) ? {style: attributes.style} : {}
                }
            };
        },

        addProseMirrorPlugins() {
            return [
                ...this.parent?.() ?? [], //per des 2025 impl ikke TableCell addProseMirrorPlugins
                TabellCellePlugin
            ];
        },
    });
}

/** Plugin som sørger for masere TD-innslag.
 * - fjerner valign attributt og legger til angitt verdi som vertical-align style attributt (tabeller fra word bruker valign) */
const TabellCellePlugin = new Plugin({
    key: new PluginKey('tabellcelle-plugin'),
    props: {
        transformPastedHTML: (html: string): string => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, `text/html`);
            const celler = doc.querySelectorAll<HTMLTableCellElement>('td[valign]');
            celler.forEach(cell => {
                const valign = cell.getAttribute('valign');
                if (erIkkeTomStreng(valign))
                    cell.style.verticalAlign = valign;
                cell.removeAttribute('valign');
            })

            return doc.documentElement.outerHTML;
        }
    }
});


export function valgteTabellCeller(valgteCeller: CellSelection): NodeOgPosisjon[] {
    if (!(valgteCeller instanceof CellSelection)) return [];
    const celleNoder: NodeOgPosisjon[] = [];
    valgteCeller.forEachCell((node: ProseMirrorNode, posisjon) => celleNoder.push({node, posisjon}));
    return celleNoder;
}

// typedefinisjoner

export type NodeOgPosisjon = { node: ProseMirrorNode; posisjon: number; }

interface TabellcelleKommandoer<ReturnType> {
    /** Setter selection til angitt celle.
     * @param cellePosisjon posisjonen til en celle som skal markeres om valgt */
    velgCelle: (celle: number) => ReturnType;

    /** Setter selection til alle cellenen i raden som angitt celle ligger i.
     * @param cellePosisjon posisjonen til en celle i aktuell rad */
    velgRad: (celle: number) => ReturnType;

    /** Setter selection til alle cellenen i kolonnen som angitt celle ligger i.
     * @param cellePosisjon posisjonen til en celle i aktuell kolonne */
    velgKolonne: (celle: number) => ReturnType;

    /** Setter selection til alle cellenen i kolonnen som angitt celle ligger i.
     * @param cellePosisjon posisjonen til en celle i aktuell kolonne */
    velgAlleCeller: (cellePosisjon: number) => ReturnType;
}

//for at kommandoene skal dukke opp i editor ts typen
declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        tableCell: TabellcelleKommandoer<ReturnType>;
    }
}

